<template>
  <div
    class="muit-report-container"
    v-loading="loading"
    element-loading-text="正在生成专属报告..."
  >
    <div class="nav-bar">
      <i class="el-icon-arrow-left back-icon" @click="goBack()"></i>
      <!-- <span class="title">多元智能测试</span> -->
      <div class="capsule"></div>
    </div>

    <div v-if="!loading" class="main-content">
      <div class="header-card">
        <h2 class="report-title">您的多元智能测评报告</h2>
        <p class="report-time">生成于 · {{ reportData.createTime }}</p>

        <div class="type-content">
          <div class="type-section">
            <div class="type-label">
              <span>您的多元智能测评优势类型是：</span>
            </div>
            <div class="type-value">{{ reportData.advantageType }}</div>
          </div>

          <div class="type-section">
            <div class="type-label">
              <span>您的多元智能测评劣势类型是：</span>
            </div>
            <div class="type-value">{{ reportData.disadvantageType }}</div>
          </div>

          <p class="type-description">{{ reportData.brief }}</p>
        </div>
      </div>

      <div class="chart-card">
        <h3 class="chart-title">智能类型八边形</h3>
        <div class="radar-chart" ref="radarChart"></div>
      </div>
      <div class="info-content">
        <div class="detail-card">
          <h3 class="detail-title one" style="padding-bottom: 0.5rem">
            数据基于多元智能测评
          </h3>

          <h3 class="detail-title more">职业特征</h3>
          <div class="info-item">
            <h4 class="sub-title">性格特征</h4>
            <ul class="feature-list block-list">
              <li
                v-for="(item, index) in reportData.personalityTraits"
                :key="index"
              >
                {{ item }}
              </li>
            </ul>

            <h4 class="sub-title">优势特点</h4>
            <ul class="feature-list block-list">
              <li
                v-for="(item, index) in reportData.advantages"
                :key="`adv-${index}`"
              >
                {{ item }}
              </li>
            </ul>
          </div>
        </div>

        <div class="detail-card">
          <h3 class="detail-title more">职业建议</h3>
          <div class="info-item">
            <p class="paragraph">{{ reportData.careerAdviceText }}</p>
          </div>
        </div>

        <div class="detail-card">
          <h3 class="detail-title more">发展建议</h3>
          <div class="info-item">
            <h4 class="sub-title">潜在挑战</h4>
            <ul class="feature-list block-list">
              <li
                v-for="(item, index) in reportData.challenges"
                :key="`cha-${index}`"
              >
                {{ item }}
              </li>
            </ul>

            <h4 class="sub-title">发展建议</h4>
            <ul class="feature-list block-list">
              <li
                v-for="(item, index) in reportData.developmentTips"
                :key="`tip-${index}`"
              >
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bar">
      <button class="btn-left" @click="shareLink">分享链接</button>
      <button class="btn-right" @click="restartTest">再测一次</button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as echarts from "echarts";
import { copyText } from "@/utils/clipboard";

function createEmptyReportData() {
  return {
    createTime: "",
    advantageType: "",
    disadvantageType: "",
    brief: "",
    intelligenceScores: [],
    personalityTraits: [],
    advantages: [],
    careerAdviceText: "",
    challenges: [],
    developmentTips: [],
  };
}

const radarTypes = [
  "语言智能",
  "自然观察智能",
  "内省智能",
  "人际交往智能",
  "音乐智能",
  "身体—动觉智能",
  "视觉—空间智能",
  "逻辑—数学智能",
];

export default {
  name: "MuitResult",
  data() {
    return {
      loading: true,
      reportData: createEmptyReportData(),
      chartInstance: null,
    };
  },
  created() {
    this.initData();
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    initData() {
      let openid = "";
      let wjId =
        this.$route.query.wjId || localStorage.getItem("muitTestWjId") || "3";

      if (this.$route && this.$route.query && this.$route.query.openid) {
        openid = this.$route.query.openid;
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        openid =
          urlParams.get("openid") ||
          localStorage.getItem("openId") ||
          localStorage.getItem("openid") ||
          "";
        wjId = urlParams.get("wjId") || wjId;
      }

      if (!openid || !wjId) {
        this.$message.error("缺少必要参数，无法获取报告");
        this.loading = false;
        return;
      }

      this.fetchReportData(openid, wjId);
    },

    colorByType(type) {
      const colorMap = {
        语言智能: "#f5b847",
        "逻辑—数学智能": "#52a7ff",
        "视觉—空间智能": "#58be6a",
        音乐智能: "#b07df7",
        "身体—动觉智能": "#ff9f55",
        人际交往智能: "#f26d9f",
        内省智能: "#6cc9c3",
        自然观察智能: "#7bcf56",
      };
      return colorMap[type] || "#78b887";
    },

    normalizeScoreList(scoreData) {
      const legacyMap = {
        language: "语言智能",
        logic: "逻辑—数学智能",
        space: "视觉—空间智能",
        music: "音乐智能",
        body: "身体—动觉智能",
        interpersonal: "人际交往智能",
        intrapersonal: "内省智能",
        nature: "自然观察智能",
      };
      const scoreMap = {};

      if (Array.isArray(scoreData)) {
        scoreData.forEach((item) => {
          const typeName =
            item && item.xxType ? String(item.xxType).trim() : "";
          if (!typeName) return;
          scoreMap[typeName] = Number(item.num) || 0;
        });
      } else if (scoreData && typeof scoreData === "object") {
        Object.keys(scoreData).forEach((key) => {
          const typeName = legacyMap[key] || key;
          scoreMap[typeName] = Number(scoreData[key]) || 0;
        });
      }

      return radarTypes.map((typeName) => ({
        name: typeName,
        score: scoreMap[typeName] || 0,
        color: this.colorByType(typeName),
      }));
    },

    parseDetailText(text) {
      if (!text) return [];
      return String(text)
        .split(/\n+/)
        .map((item) => item.trim())
        .filter((item) => item)
        .map((item) => item.replace(/^\d+[\.、]\s*/, ""));
    },

    extractDetails(detailList) {
      const detailMap = {};
      (detailList || []).forEach((item) => {
        detailMap[item.dimension] = this.parseDetailText(item.detail);
      });
      return detailMap;
    },

    async fetchReportData(openid, wjId) {
      this.loading = true;
      try {
        const apiUrl = `http://59.110.51.52:8010/system/web/getsurvey?openid=${encodeURIComponent(
          openid
        )}&wjId=${wjId}`;

        const response = await axios.get(apiUrl);
        const resData = response.data;

        if (resData.code !== 200) {
          this.$message.error(resData.msg || "获取数据失败");
          return;
        }

        const apiData = resData.data || {};
        const detailMap = this.extractDetails(apiData.detail);
        const careerAdviceList = detailMap["职业建议"] || [];

        this.reportData = {
          createTime: this.getCurrentTime(),
          advantageType: apiData.youshi || "",
          disadvantageType: apiData.lieshi || "",
          brief: apiData.jianjie || apiData.description || "",
          intelligenceScores: this.normalizeScoreList(apiData.score),
          personalityTraits:
            detailMap["性格特征"] || apiData.personalityTraits || [],
          advantages: detailMap["优势特点"] || apiData.advantages || [],
          careerAdviceText:
            careerAdviceList.join("、") || apiData.careerAdvice || "",
          challenges: detailMap["潜在挑战"] || apiData.challenges || [],
          developmentTips:
            detailMap["发展建议"] || apiData.developmentTips || [],
        };
      } catch (error) {
        console.error("接口请求失败:", error);
        this.$message.error("网络请求失败，请稍后再试");
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initRadarChart();
        });
      }
    },

    initRadarChart() {
      if (!this.$refs.radarChart) return;

      const scores = (this.reportData.intelligenceScores || []).filter(
        (item) => {
          return item && item.name;
        }
      );
      if (!scores.length) return;

      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }

      this.chartInstance = echarts.init(this.$refs.radarChart);
      const maxScore = Math.max(
        10,
        ...scores.map((item) => Number(item.score) || 0)
      );
      const indicator = scores.map((item) => ({
        name: item.name,
        max: maxScore,
      }));
      const scoreMap = {};
      const dataValues = scores.map((item) => {
        scoreMap[item.name] = Number(item.score) || 0;
        return scoreMap[item.name];
      });

      this.chartInstance.setOption({
        animation: true,
        tooltip: { show: false },
        radar: {
          indicator: indicator,
          radius: "48%",
          center: ["46%", "50%"],
          shape: "polygon",
          splitNumber: 5,
          axisNameGap: 16,
          axisName: {
            formatter(value) {
              const score = scoreMap[value] || 0;
              return `{name|${value}}\n{score|${score}分}`;
            },
            rich: {
              name: {
                color: "#8d9490",
                fontSize: 14,
                lineHeight: 18,
                fontWeight: 500,
                align: "center",
              },
              score: {
                color: "#47b763",
                fontSize: 14,
                lineHeight: 18,
                fontWeight: 700,
                align: "center",
              },
            },
          },
          splitLine: {
            lineStyle: {
              color: "#dde7dd",
              width: 1,
            },
          },
          splitArea: {
            areaStyle: {
              color: ["#ffffff", "#fbfdfb"],
            },
          },
          axisLine: {
            lineStyle: {
              color: "#dde7dd",
            },
          },
        },
        series: [
          {
            type: "radar",
            symbol: "circle",
            symbolSize: 4,
            data: [
              {
                value: dataValues,
                areaStyle: {
                  color: "rgba(89, 200, 110, 0.18)",
                },
                lineStyle: {
                  color: "#59c86e",
                  width: 2,
                },
                itemStyle: {
                  color: "#59c86e",
                },
              },
            ],
          },
        ],
      });
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },

    getCurrentTime() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      const h = String(now.getHours()).padStart(2, "0");
      const min = String(now.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}`;
    },

    goBack() {
      this.$router.push("/muit-home");
    },

    shareLink() {
      // 构建分享链接
      const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

      copyText(shareUrl)
        .then(() => {
          this.$message.success("链接已复制，去分享给好友吧！");
        })
        .catch((err) => {
          console.error("复制失败:", err);
          this.$message.error("复制失败，请手动复制链接");
        });
    },

    restartTest() {
      this.$router.push("/muit-qa");
    },
  },
};
</script>

<style scoped lang="scss">
.muit-report-container {
  max-width: 375px;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(180deg, #86d566, #4e9154, #f8fff8);

  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  padding-bottom: 84px;
}

.nav-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
}

.back-icon {
  font-size: 20px;
  color: #fff;
  cursor: pointer;
}

.title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.main-content {
  padding: 8px 10px 0;
}

.header-card {
  border-radius: 14px;
  /* background: linear-gradient(180deg, #5fcf69 0%, #57c667 100%); */
  color: #fff;
  text-align: center;
  padding: 14px 0;
  margin-bottom: 15px;
  .type-content {
    position: relative;
    overflow: hidden;
    background-color: #fff;
    padding: 28px 0;
    border-radius: 10px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 44px;
      background: linear-gradient(
        180deg,
        rgba(7, 178, 66, 0.2) 0%,
        rgba(7, 178, 66, 0.08) 52%,
        rgba(7, 178, 66, 0) 100%
      );
      pointer-events: none;
    }
  }
}

.report-title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
}

.report-time {
  margin: 10px 0 20px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.96);
}

.type-section {
  background: #fff;
  border-radius: 10px;
  padding: 9px 8px;
  margin-bottom: 15px;
}

.type-label {
  position: relative;
  width: max-content;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  color: #000;
  font-weight: 800;
  margin-bottom: 8px;
  span {
    &::before {
      content: "";
      position: absolute;
      left: -10px;
      top: 2px;
      width: 6px;
      height: 14px;
      background-color: #30bf61;
      border-radius: 10px;
    }
  }
}

.type-icon {
  font-size: 16px;

  span {
    font-size: 16px;
  }
}

.type-value {
  font-size: 20px;
  color: #07b242;
  font-weight: 800;
  line-height: 1.6;
}

.type-description {
  margin: 0;
  background: #f5f5f5;
  border-radius: 10px;
  padding: 10px 10px;
  text-align: left;
  color: #6f7f74;
  font-size: 13px;
  line-height: 1.55;
  width: calc(100% - 20px);
  margin-left: 10px;
}

.chart-card,
.detail-card {
  background: #fff;
  border-radius: 12px;
  padding: 28px 16px;
  margin-bottom: 5px;
}
.detail-card {
  padding-bottom: 10px !important;
  padding: 10px 16px;
}
.chart-card {
  margin-bottom: 30px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 44px;
    background: linear-gradient(
      180deg,
      rgba(7, 178, 66, 0.2) 0%,
      rgba(7, 178, 66, 0.08) 52%,
      rgba(7, 178, 66, 0) 100%
    );
    pointer-events: none;
  }
}

.chart-title {
  position: relative;
  margin: 0 0 6px;
  text-align: center;
  font-size: 16px;
  color: #000;
  font-weight: 700;
  width: max-content;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 16px;
  &::before {
    content: "";
    position: absolute;
    left: -10px;
    top: 2px;
    width: 6px;
    height: 14px;
    background-color: #30bf61;
    border-radius: 16px;
  }
}

.radar-chart {
  width: 100%;
  height: 268px;
}

.info-content {
  position: relative;
  background-color: #fff;
  border-radius: 20px;
  padding: 20px 0;
  .info-item {
    background-color: #f5f5f5;
    padding: 20px 20px;
    border-radius: 10px;
  }
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 44px;
    background: linear-gradient(
      180deg,
      rgba(7, 178, 66, 0.2) 0%,
      rgba(7, 178, 66, 0.08) 52%,
      rgba(7, 178, 66, 0) 100%
    );
    pointer-events: none;
  }
}

.detail-title {
  margin: 0 0 8px;
  margin-bottom: 18px;
  text-align: center;
  font-size: 18px;
  color: #2d3a4b;
  font-weight: 800;
  position: relative;
  width: max-content;
  left: 50%;
  transform: translateX(-50%);
  &.more {
    margin-bottom: 16px;
    position: relative;
  }
  &.more::before {
    content: "";
    position: absolute;
    left: 0x;
    bottom: -4px;
    width: 100%;
    height: 6px;
    background-color: #81d49f;
    z-index: -10;
  }
}
.detail-title.one {
  margin-bottom: 18px;
  &::before {
    content: "";
    position: absolute;
    left: -10px;
    top: 2px;
    width: 6px;
    height: 14px;
    background-color: #30bf61;
    border-radius: 16px;
  }
}

.sub-title {
  margin: 12px 0 10px !important;
  font-size: 16px;
  color: #2f3b4f;
  font-weight: 700;
}

.feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.feature-list li {
  position: relative;
  padding: 3px 0 3px 10px;
  font-size: 13px;
  color: #5a6571;
  line-height: 1.6;
}

.feature-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #5cb85c;
}

.paragraph {
  margin: 0;
  font-size: 14px;
  color: #5d6872;
  line-height: 1.7;
}

.footer-bar {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  max-width: 375px;
  padding: 10px;
  display: flex;
  gap: 10px;
  background: rgba(246, 255, 245, 0.96);
}

.btn-left,
.btn-right {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  border: none;
  font-size: 15px;
  font-weight: 700;
}

.btn-left {
  background: #c9ced2;
  color: #fff;
}

.btn-right {
  background: linear-gradient(90deg, #8adb58 0%, #58c875 100%);
  color: #fff;
}
</style>
